import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { DocumentTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PhoneIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Validation constants
const MIN_RENT = 1000;
const MAX_RENT = 1000000;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

// Indian phone number regex (10 digits, optionally with +91 or 0 prefix)
const PHONE_REGEX = /^(\+91[\-\s]?)?[0]?(91)?[6789]\d{9}$/;

// Validation helper functions
const validatePhone = (phone) => {
  const cleanPhone = phone.replace(/[\s\-]/g, '');
  return PHONE_REGEX.test(cleanPhone);
};

const validateRent = (rent) => {
  const rentNum = parseFloat(rent);
  if (isNaN(rentNum) || rentNum < MIN_RENT) {
    return { valid: false, message: `Rent must be at least ₹${MIN_RENT.toLocaleString()}` };
  }
  if (rentNum > MAX_RENT) {
    return { valid: false, message: `Rent cannot exceed ₹${MAX_RENT.toLocaleString()}` };
  }
  return { valid: true };
};

const validateLeaseDates = (startDate, endDate) => {
  if (!startDate || !endDate) return { valid: true }; // Optional fields

  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (end <= start) {
    return { valid: false, message: 'Lease end date must be after start date' };
  }

  if (end < today) {
    return { valid: false, message: 'Your lease appears to have expired. Please provide current lease details.' };
  }

  return { valid: true };
};

const validateFile = (file) => {
  if (!file) {
    return { valid: false, message: 'Please upload a rent agreement file' };
  }

  if (file.size === 0) {
    return { valid: false, message: 'The selected file is empty. Please upload a valid document.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, message: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB` };
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return { valid: false, message: 'Please upload a PDF, JPEG, JPG, or PNG file' };
  }

  return { valid: true };
};

const Verification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Tab state - 'agreement' or 'landlord'
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'agreement');

  const [formData, setFormData] = useState({
    monthlyRent: '',
    propertyAddress: '',
    landlordName: '',
    landlordEmail: '',
    landlordPhone: '',
    leaseStartDate: '',
    leaseEndDate: '',
    numCoTenants: '1' // Default: single tenant (no sharing)
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/verification/status`);

      // DEBUG: Log full API response
      console.log('=== VERIFICATION STATUS DEBUG ===');
      console.log('Full API Response:', JSON.stringify(response.data, null, 2));
      console.log('User from API:', response.data.user);
      console.log('Agreement from API:', response.data.agreement);
      console.log('User.verificationStatus:', response.data.user?.verificationStatus);
      console.log('User.isVerified:', response.data.user?.isVerified);
      console.log('Agreement.status:', response.data.agreement?.status);
      console.log('=================================');

      setVerificationStatus(response.data);

      // Pre-fill form if agreement exists
      if (response.data.agreement) {
        const details = response.data.agreement.rentalDetails;
        console.log('Pre-filling form with:', details);
        setFormData({
          monthlyRent: details.monthlyRent || '',
          propertyAddress: details.propertyAddress || '',
          landlordName: details.landlordName || '',
          landlordEmail: details.landlordEmail || '',
          landlordPhone: details.landlordPhone || '',
          leaseStartDate: details.leaseStartDate ? new Date(details.leaseStartDate).toISOString().split('T')[0] : '',
          leaseEndDate: details.leaseEndDate ? new Date(details.leaseEndDate).toISOString().split('T')[0] : '',
          numCoTenants: details.numCoTenants || '1'
        });
      } else {
        console.log('No agreement found - user has not submitted verification yet');
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validation = validateFile(selectedFile);
      if (!validation.valid) {
        setError(validation.message);
        e.target.value = ''; // Reset file input
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // File validation
    const fileValidation = validateFile(file);
    if (!fileValidation.valid) {
      setError(fileValidation.message);
      setLoading(false);
      return;
    }

    // Rent validation
    const rentValidation = validateRent(formData.monthlyRent);
    if (!rentValidation.valid) {
      setError(rentValidation.message);
      setLoading(false);
      return;
    }

    // Address validation
    if (!formData.propertyAddress || formData.propertyAddress.trim().length < 10) {
      setError('Please enter a complete property address (at least 10 characters)');
      setLoading(false);
      return;
    }

    // Lease dates validation
    const dateValidation = validateLeaseDates(formData.leaseStartDate, formData.leaseEndDate);
    if (!dateValidation.valid) {
      setError(dateValidation.message);
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('rentAgreement', file);
      formDataToSend.append('monthlyRent', formData.monthlyRent);
      formDataToSend.append('propertyAddress', formData.propertyAddress);
      formDataToSend.append('landlordName', formData.landlordName);
      formDataToSend.append('landlordEmail', formData.landlordEmail);
      formDataToSend.append('landlordPhone', formData.landlordPhone);
      formDataToSend.append('leaseStartDate', formData.leaseStartDate);
      formDataToSend.append('leaseEndDate', formData.leaseEndDate);
      formDataToSend.append('numCoTenants', formData.numCoTenants);

      const response = await axios.post(
        `${API_URL}/verification/upload-agreement`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess(response.data.message);
      fetchVerificationStatus();
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload agreement');
    } finally {
      setLoading(false);
    }
  };

  const handleLandlordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Landlord name validation
    if (!formData.landlordName || formData.landlordName.trim().length < 2) {
      setError('Please enter a valid landlord name');
      setLoading(false);
      return;
    }

    // Phone validation
    if (!formData.landlordPhone) {
      setError('Landlord phone number is required');
      setLoading(false);
      return;
    }

    if (!validatePhone(formData.landlordPhone)) {
      setError('Please enter a valid Indian phone number (10 digits)');
      setLoading(false);
      return;
    }

    // Rent validation
    const rentValidation = validateRent(formData.monthlyRent);
    if (!rentValidation.valid) {
      setError(rentValidation.message);
      setLoading(false);
      return;
    }

    // Address validation
    if (!formData.propertyAddress || formData.propertyAddress.trim().length < 10) {
      setError('Please enter a complete property address (at least 10 characters)');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/verification/landlord-contact`,
        {
          landlordName: formData.landlordName,
          landlordPhone: formData.landlordPhone,
          landlordEmail: formData.landlordEmail,
          monthlyRent: formData.monthlyRent,
          propertyAddress: formData.propertyAddress
        }
      );

      setSuccess(response.data.message || 'Landlord details submitted! We will contact your landlord for verification.');
      fetchVerificationStatus();
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit landlord details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircleIcon className="h-8 w-8 text-primary-600" />;
      case 'rejected':
        return <XCircleIcon className="h-8 w-8 text-red-600" />;
      case 'pending':
        return <ClockIcon className="h-8 w-8 text-yellow-600" />;
      default:
        return <DocumentTextIcon className="h-8 w-8 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Pending Review';
      default:
        return 'Not Submitted';
    }
  };

  if (loadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const status = verificationStatus?.user?.verificationStatus || 'unverified';

  // DEBUG: Log computed status
  console.log('=== COMPUTED STATUS DEBUG ===');
  console.log('Computed status:', status);
  console.log('Shows pending warning:', status === 'pending');
  console.log('Shows verified success:', status === 'verified');
  console.log('============================');

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Tenant Verification</h1>

        {/* Status Card */}
        {verificationStatus && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex items-center space-x-4">
              {getStatusIcon(status)}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Verification Status: {getStatusText(status)}
                </h2>
                {verificationStatus.agreement && (
                  <p className="text-sm text-gray-600 mt-1">
                    Submitted on {new Date(verificationStatus.agreement.uploadedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {verificationStatus.agreement?.reviewNotes && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-1">Review Notes:</p>
                <p className="text-sm text-gray-600">{verificationStatus.agreement.reviewNotes}</p>
              </div>
            )}

            {/* Pending warning */}
            {status === 'pending' && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Your verification is under review.</strong> You can update your submission below if needed, but this may restart the review process.
                </p>
              </div>
            )}

            {/* Verified success message */}
            {status === 'verified' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>You're verified!</strong> You can now pay rent and earn rewards through LOCU.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tab Selector */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('agreement')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold transition ${
                activeTab === 'agreement'
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DocumentTextIcon className="h-5 w-5" />
              Upload Agreement
            </button>
            <button
              onClick={() => setActiveTab('landlord')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold transition ${
                activeTab === 'landlord'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <PhoneIcon className="h-5 w-5" />
              Landlord Contact
            </button>
          </div>

          <div className="p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              {activeTab === 'agreement'
                ? (status === 'unverified' || status === 'rejected' ? 'Upload Rent Agreement' : 'Update Rent Agreement')
                : 'Provide Landlord Contact'
              }
            </h2>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-6 bg-primary-50 border border-primary-200 text-primary-700 px-4 py-3 rounded">
                {success}
              </div>
            )}

            {/* Agreement Upload Tab */}
            {activeTab === 'agreement' && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* File Upload */}
                <div>
                  <label htmlFor="rentAgreement" className="block text-sm font-medium text-gray-700 mb-2">
                    Rent Agreement Document *
                  </label>
                  <input
                    type="file"
                    id="rentAgreement"
                    name="rentAgreement"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    required={status === 'unverified'}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Accepted formats: JPEG, JPG, PNG, PDF (Max 10MB).
                  </p>
                  {file && (
                    <p className="mt-2 text-sm text-primary-600 font-medium">
                      Selected: {file.name}
                    </p>
                  )}
                </div>

                {/* Monthly Rent + Co-tenants */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label htmlFor="monthlyRent" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Monthly Rent (₹) *
                    </label>
                    <input
                      type="number"
                      id="monthlyRent"
                      name="monthlyRent"
                      step="1"
                      min="1"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="25000"
                      value={formData.monthlyRent}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="numCoTenants" className="block text-sm font-medium text-gray-700 mb-2">
                      Sharing with
                    </label>
                    <select
                      id="numCoTenants"
                      name="numCoTenants"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      value={formData.numCoTenants}
                      onChange={handleChange}
                    >
                      <option value="1">Just me</option>
                      <option value="2">2 people</option>
                      <option value="3">3 people</option>
                      <option value="4">4 people</option>
                      <option value="5">5 people</option>
                      <option value="6">6 people</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Total tenants in agreement</p>
                  </div>
                </div>

                {/* Property Address */}
                <div>
                  <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Property Address *
                  </label>
                  <textarea
                    id="propertyAddress"
                    name="propertyAddress"
                    rows="3"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter full property address"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                  />
                </div>

                {/* Landlord Name */}
                <div>
                  <label htmlFor="landlordName" className="block text-sm font-medium text-gray-700 mb-2">
                    Landlord Name
                  </label>
                  <input
                    type="text"
                    id="landlordName"
                    name="landlordName"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Landlord or property manager name"
                    value={formData.landlordName}
                    onChange={handleChange}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {loading ? 'Uploading...' : status === 'verified' ? 'Update Agreement' : 'Submit for Verification'}
                </button>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Your rent agreement will be reviewed by our team.
                    Once verified, you'll be able to pay rent and earn rewards through LOCU.
                  </p>
                </div>
              </form>
            )}

            {/* Landlord Contact Tab */}
            {activeTab === 'landlord' && (
              <form onSubmit={handleLandlordSubmit} className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> Provide your landlord's contact details and we'll reach out to verify your tenancy. This usually takes 1-2 business days.
                  </p>
                </div>

                {/* Landlord Name */}
                <div>
                  <label htmlFor="landlordNameTab" className="block text-sm font-medium text-gray-700 mb-2">
                    Landlord / Owner Name *
                  </label>
                  <input
                    type="text"
                    id="landlordNameTab"
                    name="landlordName"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Full name of landlord or property owner"
                    value={formData.landlordName}
                    onChange={handleChange}
                  />
                </div>

                {/* Landlord Phone */}
                <div>
                  <label htmlFor="landlordPhoneTab" className="block text-sm font-medium text-gray-700 mb-2">
                    Landlord Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="landlordPhoneTab"
                    name="landlordPhone"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+91 98765 43210"
                    value={formData.landlordPhone}
                    onChange={handleChange}
                  />
                  <p className="mt-1 text-xs text-gray-500">We'll call or WhatsApp this number to verify your tenancy</p>
                </div>

                {/* Landlord Email (Optional) */}
                <div>
                  <label htmlFor="landlordEmailTab" className="block text-sm font-medium text-gray-700 mb-2">
                    Landlord Email (Optional)
                  </label>
                  <input
                    type="email"
                    id="landlordEmailTab"
                    name="landlordEmail"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="landlord@email.com"
                    value={formData.landlordEmail}
                    onChange={handleChange}
                  />
                </div>

                {/* Monthly Rent + Co-tenants */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label htmlFor="monthlyRentTab" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Monthly Rent (₹) *
                    </label>
                    <input
                      type="number"
                      id="monthlyRentTab"
                      name="monthlyRent"
                      step="1"
                      min="1"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="25000"
                      value={formData.monthlyRent}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="numCoTenantsTab" className="block text-sm font-medium text-gray-700 mb-2">
                      Sharing with
                    </label>
                    <select
                      id="numCoTenantsTab"
                      name="numCoTenants"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      value={formData.numCoTenants}
                      onChange={handleChange}
                    >
                      <option value="1">Just me</option>
                      <option value="2">2 people</option>
                      <option value="3">3 people</option>
                      <option value="4">4 people</option>
                      <option value="5">5 people</option>
                      <option value="6">6 people</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Total tenants</p>
                  </div>
                </div>

                {/* Property Address */}
                <div>
                  <label htmlFor="propertyAddressTab" className="block text-sm font-medium text-gray-700 mb-2">
                    Property Address *
                  </label>
                  <textarea
                    id="propertyAddressTab"
                    name="propertyAddress"
                    rows="3"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter full property address where you're renting"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit for Verification'}
                </button>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>What happens next?</strong> Our team will contact your landlord within 1-2 business days to verify your tenancy. You'll be notified once verified.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verification;

